import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Checkbox, ListItemText, Menu, MenuItem } from '@mui/material';
import UPButton from 'components/button';
import { MouseEvent, useState } from 'react';
import { TriggerButton } from './styles';
import { UPDropdownItem } from './types';

export interface UPInlineMultiSelectDropdownProps {
  items: UPDropdownItem[];
  values: string[];
  onChange: (values: string[]) => void;
  allValue?: string;
  triggerLabel?: string;
  id?: string;
  color?: string;
}

const UPInlineMultiSelectDropdown = (props: UPInlineMultiSelectDropdownProps): JSX.Element => {
  const { items, values, onChange, allValue, triggerLabel, id, color } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [draftValues, setDraftValues] = useState<string[]>(values);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setDraftValues(values);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleApply = () => {
    onChange(draftValues);
    setAnchorEl(null);
  };

  const individualValues = items.map(i => String(i.value)).filter(v => v !== allValue);
  const isAllSelected = allValue !== undefined && draftValues.includes(allValue);

  const isSelected = (itemValue: string) => isAllSelected || draftValues.includes(itemValue);

  const handleToggle = (itemValue: string) => {
    const base = isAllSelected ? individualValues : draftValues.filter(v => v !== allValue);
    const next = base.includes(itemValue) ? base.filter(v => v !== itemValue) : [...base, itemValue];

    if (allValue === undefined) {
      setDraftValues(next);
      return;
    }
    if (next.length === 0 || next.length === individualValues.length) {
      setDraftValues([allValue]);
      return;
    }
    setDraftValues(next);
  };

  const computedLabel =
    triggerLabel ??
    (() => {
      if (values.length === 0) return '';
      const selectedLabels = items.filter(i => values.includes(String(i.value))).map(i => i.label);
      return selectedLabels.join(', ');
    })();

  return (
    <>
      <TriggerButton id={id} onClick={handleOpen} type="button" textColor={color}>
        {computedLabel}
        <ArrowDropDownIcon fontSize="small" />
      </TriggerButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {items
          .filter(item => String(item.value) !== allValue)
          .map(item => {
            const itemValue = String(item.value);
            return (
              <MenuItem key={itemValue} onClick={() => handleToggle(itemValue)} disabled={item.isDisabled}>
                <Checkbox checked={isSelected(itemValue)} size="small" />
                <ListItemText primary={item.label} />
              </MenuItem>
            );
          })}
        <MenuItem
          onClick={handleApply}
          sx={{
            justifyContent: 'center',
            '&.MuiMenuItem-root:hover': {
              backgroundColor: 'white',
            },
          }}
        >
          <UPButton text="Select" onClick={null} outlined />
        </MenuItem>
      </Menu>
    </>
  );
};

UPInlineMultiSelectDropdown.defaultProps = {
  allValue: undefined,
  triggerLabel: undefined,
  id: undefined,
  color: undefined,
};

export default UPInlineMultiSelectDropdown;
